import { TFunction } from 'i18next';
import { toLocaleDateTime } from 'utils/datetime';
import {
  getBranchRulesMarkdownReport,
  getBranchRulesRepositoryProblemText,
  GitHubBranchResult,
  GitHubBranchRule,
  GitHubBranchRuleResult,
  GitHubBranchRulesConfig,
  GitHubBranchRulesEvidence,
  GitHubBranchRulesRepository,
  readBranchRulesEvidence,
} from './IntegrationConfig_GitHub_BranchRules';

//The API's PullRequestReviewRule.MaxApprovals
export const gitHubMaxApprovals = 6;

//
// Branch rules (PLAN-FBL-003): a branch name, and what its pull request reviews must do (CIS 1.1.3, 1.1.4)
//
export class PullRequestReviewRule extends GitHubBranchRule {
  //approvals a pull request needs before it can merge, 1..6
  minimumApprovals: number = 1;

  //an owner of the changed code must approve
  requireCodeOwnerReview: boolean = false;

  //new commits dismiss the earlier approvals
  requireDismissStaleReviews: boolean = false;

  clone(): this {
    const rule = new PullRequestReviewRule();
    rule.branch = this.branch;
    rule.minimumApprovals = this.minimumApprovals;
    rule.requireCodeOwnerReview = this.requireCodeOwnerReview;
    rule.requireDismissStaleReviews = this.requireDismissStaleReviews;

    return rule as this;
  }

  isEqual(other: this): boolean {
    return (
      this.branch.trim() === other.branch.trim() &&
      this.minimumApprovals === other.minimumApprovals &&
      this.requireCodeOwnerReview === other.requireCodeOwnerReview &&
      this.requireDismissStaleReviews === other.requireDismissStaleReviews
    );
  }
}

//
// Config class: the API's GitHub_PullRequestReviewConfig
//
export class IntegrationConfig_GitHub_PullRequestReview extends GitHubBranchRulesConfig<PullRequestReviewRule> {
  defaultRules: PullRequestReviewRule[] = [new PullRequestReviewRule()];

  protected newRule(): PullRequestReviewRule {
    return new PullRequestReviewRule();
  }

  getProblems(t: TFunction<string[]>): string[] {
    const problems = super.getProblems(t);
    const rules = [this.defaultRules, ...this.repositoryRules.map((entry) => entry.rules)].flat();
    if (
      rules.some(
        (rule) =>
          !Number.isInteger(rule.minimumApprovals) ||
          rule.minimumApprovals < 1 ||
          rule.minimumApprovals > gitHubMaxApprovals,
      )
    ) {
      problems.push(t('integrations:GitHub_PullRequestReview.Rules.InvalidApprovals', { max: gitHubMaxApprovals }));
    }

    return problems;
  }

  static from(stored: unknown): IntegrationConfig_GitHub_PullRequestReview {
    const raw = (stored ?? {}) as {
      selection?: unknown;
      defaultRules?: unknown[] | null;
      repositoryRules?: unknown[];
      createChecklistItemPerRepository?: boolean;
    };
    const config = new IntegrationConfig_GitHub_PullRequestReview();
    config.readBranchRules(raw);

    return config;
  }
}

//The problem codes of the API's GitHub_PullRequestReviewHandler, stable in the evidence
export const getPullRequestReviewProblemText = (
  code: string,
  t: TFunction<string[]>,
  branch?: PullRequestReviewBranchResult,
  rule?: PullRequestReviewRuleResult,
): string => {
  switch (code) {
    case 'PullRequestNotRequired':
      return t('integrations:GitHub_PullRequestReview.Problems.PullRequestNotRequired');
    case 'TooFewApprovals':
      return t('integrations:GitHub_PullRequestReview.Problems.TooFewApprovals', {
        found: branch?.requiredApprovals ?? 0,
        required: rule?.minimumApprovals ?? 1,
      });
    case 'CodeOwnerReviewNotRequired':
      return t('integrations:GitHub_PullRequestReview.Problems.CodeOwnerReviewNotRequired');
    case 'StaleReviewsNotDismissed':
      return t('integrations:GitHub_PullRequestReview.Problems.StaleReviewsNotDismissed');
    case 'PlanNotSupported':
      return t('integrations:GitHub_PullRequestReview.Problems.PlanNotSupported');
    case 'BranchNotFound':
      return t('integrations:GitHub_PullRequestReview.Problems.BranchNotFound');
    default:
      return getBranchRulesRepositoryProblemText(code, t);
  }
};

const getYesNoText = (value: boolean, t: TFunction<string[]>): string =>
  value ? t('translation:General.Words.Yes') : t('translation:General.Words.No');

//
// Evidence classes: repository > rule > branch
//
export class PullRequestReviewBranchResult extends GitHubBranchResult {
  //Classic, Ruleset, Both or None
  source: string = 'None';

  pullRequestRequired: boolean = false;

  requiredApprovals: number = 0;

  codeOwnerReviewRequired: boolean = false;

  dismissStaleReviews: boolean = false;

  //the branch's review settings could not be read: it does not exist, or the plan has no protection for it
  get isUnknown(): boolean {
    return this.problems.includes('BranchNotFound') || this.problems.includes('PlanNotSupported');
  }

  getFoundText(t: TFunction<string[]>): string {
    if (this.isUnknown) return '-';
    if (!this.pullRequestRequired) return t('integrations:GitHub_PullRequestReview.Result.NoPullRequest');

    return t('integrations:GitHub_PullRequestReview.Result.Found', {
      approvals: this.requiredApprovals,
      codeOwners: getYesNoText(this.codeOwnerReviewRequired, t),
      stale: getYesNoText(this.dismissStaleReviews, t),
    });
  }
}

export class PullRequestReviewRuleResult extends GitHubBranchRuleResult<PullRequestReviewBranchResult> {
  minimumApprovals: number = 1;

  requireCodeOwnerReview: boolean = false;

  requireDismissStaleReviews: boolean = false;
}

export type GitHub_PullRequestReviewRepository = GitHubBranchRulesRepository<PullRequestReviewRuleResult>;

export class GitHub_PullRequestReviewEvidence extends GitHubBranchRulesEvidence<PullRequestReviewRuleResult> {
  toMarkdownReport(t: TFunction<['translation', 'integrations']>): string {
    const typedT = t as TFunction<string[]>;

    return getBranchRulesMarkdownReport<PullRequestReviewBranchResult>(
      this,
      t('integrations:GitHub_PullRequestReview.Report.SummaryHeading', {
        organization: this.organization,
        date: toLocaleDateTime(this.evaluatedAtUtc),
      }),
      (code, branch, rule) =>
        getPullRequestReviewProblemText(code, typedT, branch, rule as PullRequestReviewRuleResult),
      (branch) => branch.getFoundText(typedT),
      typedT,
    );
  }
}

//The nested shape only; a result from before it shows as "test again"
export const parse_Evidence_GitHub_PullRequestReview = (
  raw: string | undefined,
): GitHub_PullRequestReviewEvidence | undefined =>
  readBranchRulesEvidence(
    new GitHub_PullRequestReviewEvidence(),
    raw,
    (item) => {
      const branch = new PullRequestReviewBranchResult();
      branch.source = typeof item.source === 'string' ? item.source : 'None';
      branch.pullRequestRequired = item.pullRequestRequired === true;
      branch.requiredApprovals = typeof item.requiredApprovals === 'number' ? item.requiredApprovals : 0;
      branch.codeOwnerReviewRequired = item.codeOwnerReviewRequired === true;
      branch.dismissStaleReviews = item.dismissStaleReviews === true;

      return branch;
    },
    (item) => {
      const rule = new PullRequestReviewRuleResult();
      rule.minimumApprovals = typeof item.minimumApprovals === 'number' ? item.minimumApprovals : 1;
      rule.requireCodeOwnerReview = item.requireCodeOwnerReview === true;
      rule.requireDismissStaleReviews = item.requireDismissStaleReviews === true;

      return rule;
    },
  );
